import AppButton from '@nxsys/components/AppButton';
import {
  getAgencyRefMethodOptions,
  getBankFileReferenceFieldOptions,
  defaultCandidateStatus,
  defaultNonEUVerifiedIdMethodOptions,
  defaultNonUkRightToWorkStatus,
  defaultNonUKVerifiedIdMethodOptions,
  defaultPaymentFrequency,
  defaultPaymentMethod,
  defaultPaymentTerm,
  defaultPRSIEeClassesOptions,
  defaultTaxBasisIrelandOptions,
  getTimeSheetImportValidationOptions,
  defaultUKVerifiedIdMethodOptions,
  NICategoryOptions,
} from '@nxsys/constants/AppConstants';
import {
  enumMarginMethod,
  PAYMENT_MODEL_ENUM,
} from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import {
  convertArrayToString,
  countryList,
  defaultMarginMethod,
  getAppConfig,
  getArrayFromComaSeparateString,
  getSalaryCalculationMethodOptions,
} from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { some } from 'lodash';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import CompanyDefaultForm, {
  CompanyDefaultFormRef,
  CompanyDefaultFormValues,
} from './components/CompanyDefaultForm';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
};

const CompanyDefaultTab: React.FC<Props> = ({ data, updateData }) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const defaultPaymentModel = getAppConfig('paymentModels');
  const formRef = useRef<CompanyDefaultFormRef>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSubmit = useCallback(
    async (values: CompanyDefaultFormValues) => {
      try {
        setIsUpdating(true);
        const params: UpdateCompanyByIdParams = {
          taxCode: values.taxCode || '',
          taxBasis: values.taxBasis?.value || undefined,
          paymentFrequency: values.paymentFrequency?.value || null,
          NICategory: values.NICategory?.value || null,
          prsiEeClasses: values.prsiEeClasses?.value || undefined,
          candidateStatus: values.candidateStatus?.value || null,
          passProtectDoc: values.passProtectDoc ?? null,
          agencyRefMethod: values.agencyRefMethod?.value || 'NI_NUMBER',
          timesheetImportValidation:
            values.timesheetImportValidation?.value || 'AGENCY_REFERENCE_FORENAME_SURNAME',
          nonUkRightToWorkStatus: values.nonUkRightToWorkStatus?.value || null,
          subOnboarding: values.subOnboarding ?? null,
          UKVerifiedIdMethod: values.UKVerifiedIdMethod?.value || null,
          emailOnboardingNotification:
            values.emailOnboardingNotification || null,
          nonUKVerifiedIdMethod: values.nonUKVerifiedIdMethod?.value || null,
          onboarding: values.onboarding?.map((o: any) => o?.value ?? o) || [],
          bankFileReferenceField:
            values.bankFileReferenceField?.value || null,
          // Umbrella-only fields
          ...(!isBureau ? {
            paymentTerm: values.paymentTerm?.value || null,
            ...(!isIreland
              ? { paymentMethod: values.paymentMethod?.value || null }
              : {}),
            marginMethod: values.marginMethod?.value || null,
            margin: values.margin || null,
            marginCapped: values.marginCapped || null,
            // NX-5555: tristate — `?? null` so an explicit No is persisted
            autoReduceMarginForNMW: values.autoReduceMarginForNMW ?? null,
            VATRegistered: values.VATRegistered ?? null,
            isRestrictedHours: values.isRestrictedHours ?? false,
            invoicePrefix: values.invoicePrefix || '',
            invoiceNextNumber: values.invoiceNextNumber || '',
            creditNotePrefix: values.creditNotePrefix || '',
            creditNoteNextNumber: values.creditNoteNextNumber || '',
            enableAWRTracking: values.enableAWRTracking ?? null,
            enableWTR: values.enableWTR ?? null,
            notificationWeeks: getArrayFromComaSeparateString(
              values.notificationWeeks,
            ),
            enableBenefits: values.enableBenefits ?? null,
            isRequiredAttachmentExpenses:
              values.isRequiredAttachmentExpenses ?? null,
          } : {}),
        };

        if (
          values.marginMethod?.value ===
          enumMarginMethod.PERCENT_ON_TIMESHEET_VALUE
        ) {
          params.minMargin = values.minMargin || null;
        }

        if (
          some(
            defaultPaymentModel,
            (item) => item.value === PAYMENT_MODEL_ENUM.PAYE_FTH,
          )
        ) {
          params.takeHomePayPercent = values.takeHomePayPercent || null;
          params.salaryCalMethod = values.salaryCalMethod?.value || null;
          params.hoursCap = values.hoursCap || null;
        }

        if (!isBureau && typeof values.isSameAsCompany === 'boolean') {
          if (values.isSameAsCompany) {
            params.isNotSameAsCompany = false;
          } else {
            params.isNotSameAsCompany = true;
            params.invoicingData = {
              invoicingCompanyName: values.invoicingCompanyName || '',
              invoicingTradingName: values.invoicingTradingName || '',
              invoicingAddress1: values.invoicingAddress1 || '',
              invoicingAddress2: values.invoicingAddress2 || '',
              invoicingAddress3: values.invoicingAddress3 || '',
              invoicingAddress4: values.invoicingAddress4 || '',
              invoicingPostCode: values.invoicingPostCode || '',
              invoicingCity: values.invoicingCity || '',
              invoicingCounty: values.invoicingCounty || '',
              invoicingCountry: values.invoicingCountry?.value || null,
              invoicingCompanyNo: values.invoicingCompanyNo || '',
              invoicingVATRegNo: values.invoicingVATRegNo || '',
              logoUrl: data?.invoicingData?.logoUrl || '',
            };
          }
        }

        await updateData(params);
        showMessage({
          type: 'success',
          content: messages['common.updateSuccess'],
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [data, defaultPaymentModel, isBureau, messages, updateData],
  );

  return (
    <div className="CompanyDefaultTab">
      {data && (
            <>
            <CompanyDefaultForm
              formRef={formRef}
              onSubmit={handleSubmit}
              companyId={data?.id}
              defaultValues={{
                paymentTerm:
                  defaultPaymentTerm.find(
                    (item) => item.value === data?.paymentTerm,
                  ) ?? null,
                taxCode: data?.taxCode ?? '',
                taxBasis:
                  defaultTaxBasisIrelandOptions.find(
                    (item) => item.value === data?.taxBasis,
                  ) ?? null,
                paymentFrequency:
                  defaultPaymentFrequency.find(
                    (item) => item.value === data?.paymentFrequency,
                  ) ?? null,
                NICategory:
                  NICategoryOptions.find(
                    (item) => item.value === data?.NICategory,
                  ) ?? null,
                prsiEeClasses:
                  defaultPRSIEeClassesOptions.find(
                    (item) => item.value === data?.prsiEeClasses,
                  ) ?? null,
                candidateStatus:
                  defaultCandidateStatus.find(
                    (item) => item.value === data?.candidateStatus,
                  ) ?? null,
                paymentMethod:
                  defaultPaymentMethod.find(
                    (item) => item.value === data?.paymentMethod,
                  ) ?? null,
                passProtectDoc: data?.passProtectDoc ?? null,
                agencyRefMethod:
                  getAgencyRefMethodOptions(isIreland).find(
                    (item) => item.value === data?.agencyRefMethod,
                  ) ?? null,
                marginMethod:
                  defaultMarginMethod.find(
                    (item) => item.value === data?.marginMethod,
                  ) ?? null,
                margin: data?.margin ?? null,
                minMargin: data?.minMargin ?? null,
                marginCapped: data?.marginCapped ?? null,
                autoReduceMarginForNMW: data?.autoReduceMarginForNMW ?? null,
                timesheetImportValidation:
                  getTimeSheetImportValidationOptions(isBureau).find(
                    (item) =>
                      item.value === data?.timesheetImportValidation,
                  ) ?? null,
                VATRegistered: data?.VATRegistered ?? null,
                isRestrictedHours: data?.isRestrictedHours ?? null,
                isRequiredAttachmentExpenses:
                  data?.isRequiredAttachmentExpenses ?? null,
                invoicePrefix: data?.invoicePrefix ?? '',
                invoiceNextNumber: data?.invoiceNextNumber ?? '',
                creditNotePrefix: data?.creditNotePrefix ?? '',
                creditNoteNextNumber: data?.creditNoteNextNumber ?? '',
                enableAWRTracking: data?.enableAWRTracking ?? null,
                enableWTR: data?.enableWTR ?? null,
                notificationWeeks: convertArrayToString(
                  data?.notificationWeeks,
                ),
                takeHomePayPercent: data?.takeHomePayPercent ?? null,
                salaryCalMethod:
                  getSalaryCalculationMethodOptions().find(
                    (item) => item.value === data?.salaryCalMethod,
                  ) ?? null,
                hoursCap: data?.hoursCap ?? null,
                isSameAsCompany: !data?.isNotSameAsCompany,
                invoicingCompanyName:
                  data?.invoicingData?.invoicingCompanyName ?? '',
                invoicingTradingName:
                  data?.invoicingData?.invoicingTradingName ?? '',
                invoicingAddress1:
                  data?.invoicingData?.invoicingAddress1 ?? '',
                invoicingAddress2:
                  data?.invoicingData?.invoicingAddress2 ?? '',
                invoicingAddress3:
                  data?.invoicingData?.invoicingAddress3 ?? '',
                invoicingAddress4:
                  data?.invoicingData?.invoicingAddress4 ?? '',
                invoicingPostCode:
                  data?.invoicingData?.invoicingPostCode ?? '',
                invoicingCity: data?.invoicingData?.invoicingCity ?? '',
                invoicingCounty:
                  data?.invoicingData?.invoicingCounty ?? '',
                invoicingCountry:
                  countryList.find(
                    (item) =>
                      item.value ===
                      data?.invoicingData?.invoicingCountry,
                  ) ?? null,
                invoicingCompanyNo:
                  data?.invoicingData?.invoicingCompanyNo ?? '',
                invoicingVATRegNo:
                  data?.invoicingData?.invoicingVATRegNo ||
                  (isIreland ? 'IE' : ''),
                enableBenefits: data?.enableBenefits ?? null,
                bankFileReferenceField:
                  getBankFileReferenceFieldOptions(isBureau).find(
                    (item) =>
                      item.value === data?.bankFileReferenceField,
                  ) ?? getBankFileReferenceFieldOptions(isBureau)[0],
                nonUkRightToWorkStatus:
                  defaultNonUkRightToWorkStatus.find(
                    (item) =>
                      item.value === data?.nonUkRightToWorkStatus,
                  ) ?? null,
                subOnboarding: data?.subOnboarding ?? null,
                UKVerifiedIdMethod:
                  defaultUKVerifiedIdMethodOptions.find(
                    (item) => item.value === data?.UKVerifiedIdMethod,
                  ) ?? null,
                emailOnboardingNotification:
                  data?.emailOnboardingNotification ?? '',
                nonUKVerifiedIdMethod:
                  (isIreland
                    ? defaultNonEUVerifiedIdMethodOptions
                    : defaultNonUKVerifiedIdMethodOptions
                  ).find(
                    (item) => item.value === data?.nonUKVerifiedIdMethod,
                  ) ?? null,
                onboarding: (data?.onboarding ?? []).filter(
                  (step: string) =>
                    !isIreland ||
                    !['ADD_P45_DETAILS', 'ADD_P45_STARTER_DECLARATION'].includes(step),
                ),
              }}
            />
            <div className="flex items-center justify-start gap-3 pt-4">
              <AppButton
                type="primary"
                name={messages['common.save']}
                leftIcon={
                  <Icons.IconCheckCircle
                    color={defaultTheme.theme.palette.white}
                  />
                }
                onClick={() => formRef.current?.submit()}
                loading={isUpdating}
              />
            </div>
            </>
      )}
    </div>
  );
};

export default CompanyDefaultTab;
